import React, { useEffect, useMemo, useState } from 'react'

import { Icon, Stack, colors as zetColors } from '@zet/design-system'
import Pressable from '@zet/design-system/pressable'

import { DropDown } from '../dropDown'
import { TextInput } from '../textInput'

interface Option {
  optionId: string
  optionValue: string
}

interface SelectDropDownProps {
  label?: string
  placeholder?: string
  onChange: (value: string) => void
  onBlur: () => void
  value?: string
  errorMessage?: string
  isRequired?: boolean
  dropdownMode?: 'single' | 'multi'
  isSearchable?: boolean
  searchEndPoint?: string
  options?: Option[]
  fieldId?: string
  dropOptions?: any
}

export const SelectDropDown: React.FC<SelectDropDownProps> = ({
  label = 'Select Dropdown',
  placeholder = 'Select',
  onChange,
  onBlur,
  value = '',
  errorMessage,
  isRequired = true,
  dropdownMode = 'single',
  isSearchable = false,
  searchEndPoint,
  options = [],
  fieldId,
  dropOptions,
}) => {
  const [selectedValue, setSelectedValue] = useState<string>(value)
  const [dropDownVisible, setDropDownVisible] = useState<boolean>(false)

  const onDismiss = () => {
    setDropDownVisible(false)
  }

  useEffect(() => {
    if (value) {
      setSelectedValue(value)
    }
  }, [value])

  const setOptionOnSelect = (val: string, displayText: string) => {
    if (isSearchable && !options.find((item) => item.optionId === val)) {
      options.push({ optionId: val, optionValue: displayText })
    }
  }

  const onSelect = (val: string, displayText: string) => {
    setOptionOnSelect(val, displayText)
    setSelectedValue(val)
    onChange(val)
    onBlur()
  }

  const getDisplayText = useMemo(() => {
    const selectedObj = options.find(
      (option) => option.optionId === selectedValue
    )
    return selectedObj?.optionValue ?? value
  }, [selectedValue, options, value])

  return (
    <Stack>
      <Pressable onPress={() => setDropDownVisible(true)}>
        <TextInput
          isEditable={false}
          placeholder={placeholder}
          onBlur={onBlur}
          iconRight={
            <Icon
              type="ArrowDown"
              color={zetColors.grays[80]}
              className="mr-1 mt-1"
            />
          }
          iconLeft={'WorkOutlined'}
          value={
            dropdownMode === 'multi'
              ? selectedValue.toString().substring(0, 35)
              : getDisplayText
          }
          label={label}
          isRequired={isRequired}
          errorMessage={errorMessage}
          disabled={false}
          pointerEvents="none"
        />
      </Pressable>
      <DropDown
        label={label}
        dropdownMode={dropdownMode}
        isSearchable={isSearchable}
        onDismiss={onDismiss}
        selectedValue={selectedValue}
        setDropDownVisible={setDropDownVisible}
        dropDownVisible={dropDownVisible}
        searchEndPoint={searchEndPoint}
        options={options}
        fieldId={fieldId}
        onSelect={onSelect}
        dropOptions={dropOptions}
      />
    </Stack>
  )
}
